import { getApiUrl } from '../lib/runtimeConfig';

const API_BASE_URL = getApiUrl() || 'http://localhost:5001';

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

class ApiClient {
  private baseUrl: string;
  private getToken: (() => Promise<string | null>) | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setTokenProvider(provider: () => Promise<string | null>) {
    this.getToken = provider;
  }

  /**
   * Returns the current access token (or null when unauthenticated).
   * Useful for code paths that need the raw token — e.g. attaching to a
   * non-JSON fetch the apiClient doesn't model directly (HLS streams, etc.).
   */
  async resolveToken(): Promise<string | null> {
    return this.getToken ? this.getToken() : null;
  }

  private buildUrl(path: string, params?: Record<string, string | number | boolean | undefined>): string {
    const url = new URL(`${this.baseUrl}${path}`, window.location.origin);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) url.searchParams.set(key, String(value));
      });
    }
    return url.toString();
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { params, ...init } = options;
    const url = this.buildUrl(path, params);

    const headers = new Headers(init.headers);
    if (!headers.has('Content-Type') && init.body && typeof init.body === 'string') {
      headers.set('Content-Type', 'application/json');
    }

    if (this.getToken) {
      const token = await this.getToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    const response = await fetch(url, { ...init, headers });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      // Subscription lock (FR-058): a 403 carrying the accessLocked key means the org's
      // trial + grace have elapsed. Surface a typed error so route guards / callers can
      // route the user to billing instead of showing a generic failure.
      if (response.status === 403 && error?.error === 'errors.subscription.accessLocked') {
        throw new AccessLockedError();
      }
      const reason =
        error.title ||
        error.message ||
        error.error ||
        error.detail ||
        'Request failed';
      throw new ApiError(response.status, reason, error);
    }

    if (response.status === 204) return undefined as T;
    return response.json();
  }

  async get<T>(path: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
    return this.request<T>(path, { method: 'GET', params });
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  async patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  async put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  async delete<T>(path: string, options?: { headers?: Record<string, string> }): Promise<T> {
    return this.request<T>(path, { method: 'DELETE', headers: options?.headers });
  }

  async upload<T>(path: string, formData: FormData): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: formData,
    });
  }

  /**
   * Multipart upload with progress events. `fetch` can't report upload progress,
   * so this drops to XHR. Mirrors `request()`'s auth + error handling (incl. the
   * subscription access-lock 403). `onProgress` receives 0–100.
   */
  uploadWithProgress<T>(
    path: string,
    formData: FormData,
    onProgress?: (percent: number) => void,
  ): Promise<T> {
    const url = this.buildUrl(path);
    const tokenPromise = this.getToken ? this.getToken() : Promise.resolve(null);

    return tokenPromise.then(
      (token) =>
        new Promise<T>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', url);
          // Do NOT set Content-Type — the browser sets the multipart boundary.
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

          xhr.upload.onprogress = (e) => {
            if (onProgress && e.lengthComputable) {
              onProgress(Math.round((e.loaded / e.total) * 100));
            }
          };

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                resolve(xhr.responseText ? JSON.parse(xhr.responseText) : (undefined as T));
              } catch {
                resolve(undefined as T);
              }
              return;
            }
            let body: Record<string, unknown> = {};
            try {
              body = JSON.parse(xhr.responseText);
            } catch {
              body = { message: xhr.statusText };
            }
            if (xhr.status === 403 && body?.error === 'errors.subscription.accessLocked') {
              reject(new AccessLockedError());
              return;
            }
            const reason =
              (body.title as string) ||
              (body.message as string) ||
              (body.error as string) ||
              (body.detail as string) ||
              'Upload failed';
            reject(new ApiError(xhr.status, reason, body));
          };

          xhr.onerror = () => reject(new ApiError(0, 'Network error during upload'));
          xhr.send(formData);
        }),
    );
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Thrown when the backend rejects a request with 403 + the subscription-lock key
 * (`errors.subscription.accessLocked`) — the org's trial expired and the grace
 * period elapsed (FR-058). Callers / route guards should send the user to billing
 * rather than show a generic error. The proactive `useSubscription().isLocked`
 * flag is the primary signal; this typed error is the per-request backstop.
 */
export class AccessLockedError extends Error {
  constructor(message = 'Subscription access locked') {
    super(message);
    this.name = 'AccessLockedError';
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
